import * as Yup from 'yup';
import { useQueryClient } from '@tanstack/react-query';
import AllForm from '../../Stateless/Form';
import {
  Box,
  Button,
  ThemeProvider,
} from '@mui/material';
import { useThemeColors } from '../../../hooks/useThemeColors';
import { useAtom } from 'jotai';
import { theme } from '../../../theme';
import { alpha } from '@mui/material/styles';
import { COLORS } from '../../../theme/themeConstants';
import { useWalletApi } from '../../../hooks/useWalletApi';
import { walletAtom } from './WalletTable';

interface FaqFormValues {
  id?: string;
  description: string;
  descriptionInArabic: string;
  walletAmount: string;
}

const validationSchema = Yup.object().shape({
  description: Yup.string()
    .required('Description Required')
    .test(
      'not-empty-or-whitespace',
      'Description cannot be empty or contain only spaces',
      (value) => {
        if (!value) return true; // Let required validation handle empty values
        return value.trim().length > 0;
      }
    )
    .test(
      'not-numbers-only',
      'Question cannot contain only numbers',
      (value) => {
        if (!value) return true; // Let required validation handle empty values
        return !/^\d+$/.test(value.trim());
      }
    ),
  descriptionInArabic: Yup.string()
    .required('Description in Arabic Required')
    .test(
      'not-empty-or-whitespace',
      'Description in Arabic cannot be empty or contain only spaces',
      (value) => {
        if (!value) return true; // Let required validation handle empty values
        return value.trim().length > 0;
      }
    )
    .test(
      'arabic-only',
      'Description in Arabic must contain only Arabic characters',
      (value) => {
        if (!value) return true; // Let required validation handle empty values
        const trimmed = value.trim();
        // Allow Arabic characters (Unicode range \u0600-\u06FF), spaces, and basic punctuation
        const arabicRegex = /^[\u0600-\u06FF\s.,'-]+$/;
        return arabicRegex.test(trimmed);
      }
    )
    .test(
      'not-numbers-only',
      'Question in Arabic cannot contain only numbers',
      (value) => {
        if (!value) return true; // Let required validation handle empty values
        return !/^\d+$/.test(value.trim());
      }
    ),
  walletAmount: Yup.string()
    .required('Wallet Amount Required')
    .matches(/^\d*\.?\d+$/, 'Wallet amount must be a valid number'),
});

interface AddWalletAmountProps {
  onCancel: () => void;
  mode?: 'add' | 'deduct';
  customer?: any;
}

const AddWalletAmount = ({ onCancel, mode, customer }: AddWalletAmountProps) => {
  const [walletData] = useAtom(walletAtom);
  const { warning } = useThemeColors();

  // Use customer prop if provided, otherwise use walletData from atom
  const customerId = customer?.id || customer?.userId || walletData?.customerId || '';
  
  const { updateWallet } = useWalletApi();
  const queryClient = useQueryClient();
  const effectiveMode: 'add' | 'deduct' = mode ?? 'add';

  return (
    <>
      <ThemeProvider theme={theme}>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="flex-start"
          px={3}
          mt={2}
        >
          {/* <Typography sx={{ color: 'black' }}>
            Available Wallet Amount: ${320}
          </Typography> */}
        </Box>
        {AllForm({
          initialValues: {
            description: walletData?.question || '',
            descriptionInArabic: walletData?.questionInArabic || '',
            walletAmount: walletData?.answer || '',
          },
          validationSchema,
          onSubmit: async (values: Record<string, any>) => {
            const modifiedValues: FaqFormValues = {
              description: values.description?.trim() || '',
              descriptionInArabic: values.descriptionInArabic?.trim() || '',
              walletAmount: values.walletAmount,
            };
            try {
              await updateWallet({
                userId: customerId,
                amount: Number(modifiedValues.walletAmount),
                type: effectiveMode === 'add' ? 'credit' : 'debit',
                description: modifiedValues.description,
                paymentMethod: 'direct',
              });
              // Refresh wallet list and customer details
              await queryClient.invalidateQueries({ queryKey: ['wallet'] });
              await queryClient.invalidateQueries({ queryKey: ['getCustomersDetailsForOffers'] });
              onCancel();
            } catch (error) {
              console.error('Error submitting form:', error);
            }
          },
          fields: [
            {
              name: 'description',
              label: 'Description',
              type: 'text',
              isRequired: true,
            },
            {
              name: 'descriptionInArabic',
              label: 'Description (In Arabic)',
              type: 'text',
              isRequired: true,
            },
            {
              name: 'walletAmount',
              label: 'Wallet Amount (In JOD)',
              type: 'text',
              isRequired: true,
            },
          ],
          customButton: (formikProps: any) => (
            <Box sx={{ display: 'flex', gap: 2, px: 2 }}>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontSize: 14,
                  px: 3,
                  py: 1,
                  backgroundColor: COLORS.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(COLORS.primary.main, 0.8),
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  // Mark all fields as touched to show validation errors
                  Object.keys(formikProps.values).forEach((fieldName) => {
                    formikProps.setFieldTouched(fieldName, true, false);
                  });
                  // Check if there are validation errors
                  if (Object.keys(formikProps.errors).length === 0) {
                    formikProps.handleSubmit();
                  }
                   else {
                    // setIsFormChanged(true);
                  }
                }}
                // disabled={Boolean(walletData && !isFormChanged)}
              >
               Submit
              </Button>
              <Button
                type="button"
                variant="outlined"
                color="error"
                onClick={onCancel}
                sx={{
                  bgcolor: warning.main,
                  color: '#fff',
                  '&:hover': {
                    bgcolor: warning.dark,
                    color: '#fff',
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          ),
        })()}
      </ThemeProvider>
    </>
  );
};

export default AddWalletAmount;
